/*!
 * ymm-badges.js — upgrades product-card / product-page fit badges against the ACTIVE garage vehicle.
 * Scans [data-ymm-fit][data-sku]; when a vehicle is set, renders "✓ Fits your bike" / "Universal" / dims
 * non-fitting. With no active vehicle it leaves the element to its server-rendered default (from tags).
 * Re-runs on 'ymm:garage'. No deps.
 */
(function (w) {
  'use strict';
  // The element to dim/hide. MUST be the GRID COLUMN wrapper (.m-product-item), not the inner
  // .m-product-card — hiding the card leaves its column occupying a slot, which punches an empty
  // gap in the grid. Falls back to our own hook, then the card itself.
  function cardOf(n) {
    return n.closest('[data-ymm-card]') || n.closest('.m-product-item') || n.closest('.m-product-card') || null;
  }
  // NOTE: non-fitting cards are DIMMED (.ymm-nofit), never hidden. ?ymm_fits=1 used to hide them here and
  // in ymm-groups.js — and fitUrl() routes every bike without a landing page to /collections/all?ymm_fits=1,
  // so most drill-downs silently hid most of the catalogue. Grouping already separates them. (Joe, 2026-07-16)

  function run() {
    if (!w.YMM || !w.YMM.engine) return;
    // Same rule as ymm-groups.js: on a fits-* landing page the PAGE's bike wins over the garage. Badging
    // by the garage there put a red "✗ Doesn't fit your 2024 Indian Challenger" on cards sitting under a
    // "✓ Fits a 2024 Street Glide" heading — one card contradicting itself. pageVehicle() is null on
    // product pages and ordinary collections, so those keep badging against the garage exactly as before.
    var pageVeh = w.YMM.pageVehicle && w.YMM.pageVehicle();
    var garage = w.YMMGarage && w.YMMGarage.get();
    var veh = pageVeh || garage;
    var mine = !pageVeh || (garage && w.YMM.collectionHandle(garage) === w.YMM.collectionHandle(pageVeh));
    var poss = mine ? 'your ' : 'a ';
    var nodes = document.querySelectorAll('[data-ymm-fit][data-sku]');
    mountSlots(nodes);
    var shown = 0, nofit = 0;
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i], sku = n.getAttribute('data-sku');
      var card = cardOf(n);
      if (card) { card.classList.remove('ymm-nofit'); card.hidden = false; }
      // 'is-empty' keeps the slot's height (visibility:hidden) so cards never shift; `hidden` would collapse it.
      if (!veh) {
        if (n.getAttribute('data-default') === 'show') {
          n.className = 'ymm-badge ymm-badge--uni'; n.textContent = '✓ Universal';
          n.title = 'Universal — fits any motorcycle';
        } else { n.className = 'ymm-badge is-empty'; n.textContent = ''; n.removeAttribute('title'); }
        continue;
      }
      var v = w.YMM.verdict(sku, veh);
      // SHORT label — Joe, 2026-07-16. The badge is now a corner overlay ON the product image (matching
      // the reference), not a line under the title where nobody looked. At that size the bike name can't
      // fit, and it's redundant anyway: the group heading above already says "✓ Fits your 2024 Harley-
      // Davidson Street Glide". The full sentence moves to `title` so it's still there on hover and for
      // screen readers — the specificity isn't lost, it just stops shouting nine times per group.
      var full = shortLabel(veh);
      if (v === 'fits') {
        n.className = 'ymm-badge'; n.textContent = '✓ Fits';
        n.title = '✓ Fits ' + poss + full; shown++;
      } else if (v === 'universal') {
        n.className = 'ymm-badge ymm-badge--uni'; n.textContent = '✓ Universal';
        n.title = '✓ Universal — fits ' + poss + full; shown++;
      } else if (v === 'unknown') {
        n.className = 'ymm-badge is-empty'; n.textContent = ''; n.removeAttribute('title');   // no data → claim nothing
      } else {
        n.className = 'ymm-badge ymm-badge--nofit'; n.textContent = "✗ Doesn't fit";
        n.title = "✗ Doesn't fit " + poss + full;
        if (card) { card.classList.add('ymm-nofit'); nofit++; }
      }
    }
    try { w.document.dispatchEvent(new CustomEvent('ymm:badges', { detail: { shown: shown, nofit: nofit, hidden: 0, only: false } })); } catch (e) {}
  }
  // Full label incl. the make — "2024 Harley-Davidson Street Glide", not "2024 Street Glide".
  function shortLabel(veh) { return veh.label || [veh.year, veh.mk_name, veh.model].filter(Boolean).join(' '); }

  // Relocate each badge slot from `.m-product-card__info` (where the Liquid renders it, under the title)
  // up to the CARD ROOT, so the CSS overlay lands on the product image — Joe's reference has the flag in
  // the image corner, and under the title is where nobody looked.
  //
  // Done in JS, not Liquid: the slot is rendered by ymm-fit-badge.liquid from THREE card snippets
  // (product-card-1/2/3). Moving it there means editing all three and keeping them in step forever.
  // One move here covers every card variant, present and future.
  //
  // Done in JS, not CSS: absolute positioning needs the card root to be the nearest positioned ancestor.
  // `.m-product-card` already is — but `__info` and `__content` are too, so a slot left inside them pins
  // to THEIR corner. Re-parenting is explicit; guessing which ancestors the theme positions is not.
  //
  // Idempotent: re-render fires on every garage change, and MutationObserver re-runs on grid swaps.
  function mountSlots(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var slot = nodes[i].parentElement;
      if (!slot || slot.className.indexOf('ymm-badge-slot') < 0) continue;
      if (slot.getAttribute('data-mounted') === '1') continue;
      var root = slot.closest('.m-product-card') || cardOf(nodes[i]);
      // Only move it if the root actually holds the image — otherwise leave it where the theme put it
      // rather than park a badge somewhere arbitrary.
      if (!root || root === slot.parentElement || !root.querySelector('img')) continue;
      root.appendChild(slot);
      slot.setAttribute('data-mounted', '1');
    }
  }
  // The product-recommendations carousel ("You Might Also Like") — and any async grid — injects its cards
  // AFTER our load-time run(). ymm-groups' observer only re-badges when a vehicle is set (it bails with an
  // empty garage), and we had no observer of our own, so those carousel cards kept the SERVER-rendered
  // badge stuck in .m-product-card__content — pinned over the TITLE instead of the image corner (Joe,
  // 2026-07-17, IMG_8226: "✓ Universal fit" over the title; only universal showed because with no vehicle
  // it's the only card that renders a visible default badge). Watch for injected fit-badge cards ourselves
  // and re-run — vehicle or not; run()+mountSlots are idempotent (data-mounted guard), so this is cheap.
  var _watching = false;
  function watch() {
    if (_watching || !w.MutationObserver) return;
    _watching = true;
    var t;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if ((n.matches && n.matches('[data-ymm-fit][data-sku]')) ||
              (n.querySelector && n.querySelector('[data-ymm-fit][data-sku]'))) {
            clearTimeout(t); t = setTimeout(run, 60); return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
  w.YMMBadges = { refresh: run };
  document.addEventListener('ymm:ready', run);
  document.addEventListener('ymm:garage', run);
  document.addEventListener('DOMContentLoaded', function () { watch(); if (w.YMM) w.YMM.onReady(run); });
})(window);
